import { useState } from 'react';
import { Alert, Box, Button, TextField, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';

import { useFormik } from 'formik';
import * as yup from 'yup';

import { store } from '../../store';
import { resend, changeEmail } from '../../features/login/loginSlice';

import sanMiguelLogo from '../../assets/login-logo.png';

interface PendingProps {
  editionUrl: string;
  email: string;
  token: string;
}

export default function Pending(props: PendingProps) {
  const { token, email, editionUrl } = props;

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [newEmail, setNewEmail] = useState(false);

  const theme = useTheme();

  const validationSchema = yup.object({
    email: yup
      .string()
      .email('Ingrese un correo electrónico válido')
      .required('El correo electrónico es obligatorio'),
  });

  const formik = useFormik({
    initialValues: {
      email,
    },
    validationSchema: validationSchema,
    onSubmit: values => {
      // Reseteamos los estados
      setError(false);
      setSuccess(false);

      // Hacemos el dispatch del cambio de mail
      store
        .dispatch(changeEmail({
          token,
          email: values.email,
          url: editionUrl,
        }))
        .unwrap()
        .then(() => {
          setSuccess(true);
        })
        .catch(err => {
          console.error(err);
          setError(true);
        });
    },
  });

  const handleResend = () => {
    setError(false);
    setSuccess(false);
    setNewEmail(false);

    store.dispatch(resend(token))
      .unwrap()
      .then(() => {
        setSuccess(true);
      })
      .catch(err => {
        console.error(err);
        setError(true);
      });
  };

  const handleChangeEmail = () => {
    setError(false);
    setSuccess(false);
    setNewEmail(true);
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      sx={{ height: '50vh' }}
    >
      <img
        src={ sanMiguelLogo }
        style={{ maxWidth: '100%', height: 'auto', marginBottom: '2rem' }}
      />
      <Alert severity="info" sx={{ maxWidth: '50%' }}>
        Todavía no se verificó el correo electrónico. Por favor, revise su casilla y haga click en enlace
        correspondiente para completar con el proceso de registro.
        <br /><br />
        Si no recibió el correo electrónico haga click
        <Button
          variant="text"
          onClick={ handleResend }
          disabled={ success }
          sx={{ padding: 0 }}>aquí</Button>
        para reenviarlo o
        <Button
          variant="text"
          onClick={ handleChangeEmail }
          disabled={ success }
          sx={{ padding: 0 }}>aquí</Button>
          para cambiar la dirección si no es correcta.
        <br /><br />
        {
          newEmail
            ? <Box
                component="form"
                action="#"
                onSubmit={formik.handleSubmit}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                sx={{
                  border: `1px solid ${theme.palette.grey[300]}`,
                  padding: '2rem',
                  margin: '2rem',
                }}
                noValidate
                autoComplete="off"
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Correo Electrónico"
                  id="email"
                  name="email"
                  type="email"
                  inputProps={{ style: { textTransform: 'lowercase' } }}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  disabled={success}
                  sx={{ marginBottom: '1rem' }}
                />
                <Button
                  variant="outlined"
                  sx={{ mt: '1rem' }}
                  type="submit"
                  disabled={success}
                >
                  Cambiar Email
                </Button>
              </Box>
            : null
        }
        {
          !newEmail && success
            ? <>
                <Alert severity="success">Se reenvió correctamente el correo de verificación</Alert>
                <br /><br />
              </>
            : null
        }
        {
          !newEmail && error
            ? <>
                <Alert severity="warning">
                  Se produjo un error al enviar el correo de verificación. Por favor, intente nuevamente
                </Alert>
                <br /><br />
              </>
            : null
        }
        {
          newEmail && success
            ? <>
                <Alert severity="success">Se envió correctamente el correo de verificación a la nueva dirección</Alert>
                <br /><br />
              </>
            : null
        }
        {
          newEmail && error
            ? <>
                <Alert severity="warning">
                  Se produjo un error al cambiar el correo. Por favor, intente nuevamente
                </Alert>
                <br /><br />
              </>
            : null
        }
        <Box textAlign="center"><Link to="/login">Volver al inicio</Link></Box>
      </Alert>
    </Box>
  );
}
